export interface AgentProfile {
  id: string;
  email: string;
  phone?: string | null;
  name: string;
  status: string;
  costRate?: number;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  emailVerifiedAt?: string | null;
  phoneVerifiedAt?: string | null;
  /** Effective hierarchy + agent flag; from login payload when present */
  can_delegate_popups?: boolean;
}

export type AgentLoginSuccessData = {
  accessToken: string;
  refreshToken: string;
  agent: Record<string, unknown>;
};

export type AgentLoginOtpChallengeData = {
  needsOtp: true;
  pendingToken: string;
  channel: 'email' | 'phone';
  maskedDestination?: string;
};

export interface AgentLoginResponse {
  success: boolean;
  data?: AgentLoginSuccessData | AgentLoginOtpChallengeData;
  message?: string;
}

export function isLoginOtpChallenge(
  data: AgentLoginResponse['data']
): data is AgentLoginOtpChallengeData {
  return !!data && 'needsOtp' in data && data.needsOtp === true;
}

export interface AgentDashboardData {
  credit_balance: number;
  online_balance_net?: number;
  active_shops_count: number;
  /** Effective: super agent delegation AND agent popup send permission */
  can_delegate_popups?: boolean;
  can_create_shop_popup?: boolean;
  can_manage_shop_popup_permission?: boolean;
  default_shop_can_create_player_popup?: boolean;
  low_balance_warning_enabled?: boolean;
  low_balance_threshold_usd?: number;
  low_balance_effective_threshold_usd?: number;
  /** Agent cost rate 0–1; read-only in Settings. */
  cost_rate?: number;
  two_factor_enabled?: boolean;
  two_factor_channel?: 'email' | 'phone' | null;
  email_verified?: boolean;
  phone_verified?: boolean;
}

/** One row from GET /agent/transactions (wallet_transactions ledger). */
export interface AgentWalletLedgerCounterparty {
  ownerType: string;
  ownerId: string;
}

export interface AgentWalletLedgerEntry {
  id: string;
  type: string;
  amount: number | null;
  balanceBefore: number | null;
  balanceAfter: number | null;
  description: string | null;
  status: string;
  direction: 'in' | 'out' | 'unknown';
  counterparty: AgentWalletLedgerCounterparty | null;
  fromWalletId: string | null;
  toWalletId: string | null;
  transactionId: string | null;
  referenceId: string | null;
  referenceType: string | null;
  costRate?: number | null;
  costAmount?: number | null;
  platformFeeRate?: number | null;
  platformFeeAmount?: number | null;
  createdAt?: string;
  /** Some API responses use snake_case; prefer `createdAt` when both exist. */
  created_at?: string;
}

export interface AgentTransactionsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** Response from POST /agent/credits/purchase (SPEC-CDN-02). */
export interface AgentCreditPurchaseData {
  paymentUrl?: string | null;
  paymentAddress?: string | null;
  invoiceId?: string | null;
  transactionId?: string;
  amount: number;
  currency: string | null;
  creditAmount: number;
  endTime?: string | number | null;
  status: string;
  order?: Record<string, unknown>;
}

export interface ZeroxStaticWallet {
  id: string;
  ownerType: 'super_agent' | 'shop' | 'agent';
  ownerId: string;
  clientId: string;
  currency: string;
  address: string;
  tag?: string | null;
  enabled: boolean;
  raw?: unknown;
  created_at?: string;
  updated_at?: string;
}

export type AgentShopStatus = 'active' | 'suspended' | 'terminated';

/** GET /agent/shops/stats — date-range stats per shop for the agent (UTC bounds). */
export interface AgentShopHierarchyStatsRange {
  from: string;
  to: string;
}

export interface AgentShopHierarchyShopStat {
  id: string;
  name: string;
  status: string;
  total_users: number;
  new_users: number;
  total_deposit: number;
  total_withdrawal: number;
}

export interface AgentShopHierarchyStatsData {
  range: AgentShopHierarchyStatsRange;
  summary: {
    total_deposit: number;
    total_withdrawal: number;
    total_users: number;
    new_users: number;
  };
  shops: AgentShopHierarchyShopStat[];
}

export interface AgentShop {
  id: string;
  name: string;
  /** Shop portal login id (optional; may be null for legacy rows). */
  username?: string | null;
  email: string;
  phone: string | null;
  status: AgentShopStatus;
  costRate?: string | number;
  /** Agent cost baseline (0–1); shop cost must be strictly greater than this. */
  minShopCostRateExclusive?: number;
  createdAt?: string;
  updatedAt?: string;
  suspendedAt?: string | null;
  suspendedBy?: string | null;
  terminatedAt?: string | null;
  terminatedBy?: string | null;
  canCreatePlayerPopup?: boolean;
}

export interface AgentPlayer {
  id: string;
  username: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  shop_id: string | null;
  shop_assigned_at: string | null;
  created_at?: string;
}

/** GET /agent/players/:id — profile + balance + shop name */
export interface AgentPlayerDetail extends AgentPlayer {
  balance?: number;
  last_login_at?: string | null;
  shop_name?: string | null;
}

/** Wallet history rows (same shape as shop player transactions). */
export interface AgentPlayerWalletTransaction {
  id: string;
  type: string;
  amount: string | number;
  balanceBefore?: string | number;
  balanceAfter?: string | number;
  balance_before?: string | number;
  balance_after?: string | number;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  createdAt?: string;
}

// ===== Shop wallet ledger (CDN wallet) =====

/** wallet_transactions.type values (and legacy string fallbacks). */
export type ShopWalletTxType =
  | 'deposit'
  | 'withdrawal'
  | 'transfer'
  | 'bonus'
  | 'adjustment'
  | 'reservation'
  | 'reservation_release'
  | 'credit'
  | 'debit'
  | string;

export interface ShopLedgerPresentation {
  title: string;
  direction: 'in' | 'out' | 'neutral';
  signedAmount: number;
  counterparty: string | null;
  shortRef: string | null;
  fullId: string;
}

/** Row from GET /agent/shops/:shop_id/transactions (wallet_transactions + presentation). */
export interface ShopWalletLedgerRow {
  id: string;
  idempotencyKey?: string;
  ownerType?: string;
  ownerId?: string;
  type: ShopWalletTxType;
  amount: string | number;
  balanceBefore?: string | number;
  balanceAfter?: string | number;
  /** Some responses use snake_case */
  balance_before?: string | number;
  balance_after?: string | number;
  payerBalanceBefore?: string | number | null;
  payerBalanceAfter?: string | number | null;
  payer_balance_before?: string | number | null;
  payer_balance_after?: string | number | null;
  sourceChannel?: string | null;
  payerWallet?: { ownerType?: string; ownerId?: string } | null;
  recipientWallet?: { ownerType?: string; ownerId?: string } | null;
  referenceId?: string | null;
  referenceType?: string | null;
  businessTransactionId?: string | null;
  platformFeeRate?: string | number | null;
  platform_fee_rate?: string | number | null;
  platformFeeAmount?: string | number | null;
  platform_fee_amount?: string | number | null;
  costRate?: string | number | null;
  cost_rate?: string | number | null;
  costAmount?: string | number | null;
  cost_amount?: string | number | null;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  createdBy?: string | null;
  created_at?: string;
  createdAt?: string;
  presentation?: ShopLedgerPresentation;
}

export type AgentShopWalletSummary = {
  credit_wallet: {
    walletId: string | null;
    currency: string;
    status: string;
    balance: number;
    reserved_balance: number;
    available_balance: number;
  };
  usd_wallet: {
    walletId: string | null;
    currency: string;
    status: string;
    balance_gross: number;
    balance_net: number;
    reserved_gross: number;
    reserved_net: number;
  };
};

// ===== Shop USD wallet ledger (online balance) =====

export type ShopUsdWalletTxType =
  | 'deposit'
  | 'withdrawal'
  | 'purchase_credits'
  | 'adjustment'
  | 'reversal'
  | string;

export interface ShopUsdWalletLedgerParty {
  ownerType?: string;
  ownerId?: string | null;
  displayName?: string | null;
  roleLabel?: string;
  label?: string;
}

export interface ShopUsdWalletLedgerRow {
  id: string;
  usdWalletId: string;
  transactionId: string;
  type: ShopUsdWalletTxType;
  provider?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  amountGross: string | number;
  amountNet: string | number;
  balanceGrossBefore: string | number;
  balanceGrossAfter: string | number;
  balanceNetBefore: string | number;
  balanceNetAfter: string | number;
  metadata?: Record<string, unknown> | null;
  status?: string;
  createdAt?: string;
  created_at?: string;
  usdWallet?: {
    id?: string;
    ownerType?: string;
    ownerId?: string;
    currency?: string;
  } | null;
  fromParty?: ShopUsdWalletLedgerParty | null;
  toParty?: ShopUsdWalletLedgerParty | null;
  ledgerType?: string | null;
  paymentMethod?: string | null;
  displayLabel?: string | null;
  displayCategory?: string | null;
}

export interface ShopUsdWalletLedgerResponse {
  ownerType: 'shop';
  ownerId: string;
  currency: string;
  walletId: string | null;
  balanceGross: string | number;
  balanceNet: string | number;
  status: string;
  count: number;
  rows: ShopUsdWalletLedgerRow[];
}
